import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Eraser, Save, PenLine, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/**
 * QuotationSignaturePad — inline single-signature capture for customer acknowledgement
 * directly on the admin Quotation detail page. Mirrors DO SignaturePanel but only 1 signature.
 */
export const QuotationSignaturePad = ({ quotation, onUpdate }) => {
  const padRef = useRef(null);
  const [name, setName] = useState(quotation.customer_signature_name || '');
  const [saving, setSaving] = useState(false);

  const handleClearPad = () => padRef.current?.clear();

  const saveSignature = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      toast.error('Please sign before saving');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter the customer name');
      return;
    }
    setSaving(true);
    try {
      let dataUrl;
      try {
        dataUrl = padRef.current.getTrimmedCanvas().toDataURL('image/png');
      } catch (err) {
        try {
          dataUrl = padRef.current.toDataURL('image/png');
        } catch (err2) {
          toast.error('Could not capture signature. Please clear and try again.');
          console.error('Signature capture failed:', err, err2);
          setSaving(false);
          return;
        }
      }
      if (!dataUrl || dataUrl.length < 100) {
        toast.error('Signature appears empty. Please try again.');
        setSaving(false);
        return;
      }
      // Reuse the public signing endpoint via a generated token, OR write a small admin endpoint.
      // Cleanest path: ensure a token then PATCH it directly (same as customer flow).
      const tokenRes = await axios.post(`${BACKEND_URL}/api/public/quotations/${quotation.id}/generate-sign-token`);
      const token = tokenRes.data.sign_token;
      await axios.patch(`${BACKEND_URL}/api/public/quotations/sign/${token}`, {
        customer_signature: dataUrl,
        customer_signature_name: name.trim(),
      });
      toast.success('Customer acknowledgement captured');
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  const clearSignature = async () => {
    if (!window.confirm('Clear the existing acknowledgement signature?')) return;
    setSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/public/quotations/${quotation.id}/signature`);
      onUpdate?.();
      toast.success('Signature cleared');
    } catch {
      toast.error('Failed to clear signature');
    } finally {
      setSaving(false);
    }
  };

  // Already signed → show captured view with reset
  if (quotation.customer_signature) {
    return (
      <Card className="border-green-200 bg-green-50/40">
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-600" /> Customer Acknowledgement
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSignature}
              disabled={saving}
              data-testid="quotation-pad-reset"
            >
              <RotateCcw size={14} className="mr-1" /> Reset
            </Button>
          </div>
          <div className="bg-white border rounded-md p-2 mb-3 flex justify-center">
            <img
              src={quotation.customer_signature}
              alt="Customer signature"
              className="max-h-24 object-contain"
              data-testid="quotation-pad-image"
            />
          </div>
          <p className="text-sm text-gray-700"><span className="text-gray-500">Name:</span> <span className="font-medium">{quotation.customer_signature_name || '—'}</span></p>
          <p className="text-sm text-gray-700"><span className="text-gray-500">Signed at:</span> {fmtDate(quotation.customer_signed_at)}</p>
        </CardContent>
      </Card>
    );
  }

  // Capture mode
  return (
    <Card className="border-dashed border-2 border-blue-300">
      <CardContent className="p-4">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
          <PenLine size={16} className="text-blue-600" /> Customer Acknowledgement — Sign Here
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          Hand the device to your customer. They sign in the box below.
        </p>
        <div className="mb-3">
          <Label htmlFor="quotation-pad-name" className="text-sm">Customer Name *</Label>
          <Input
            id="quotation-pad-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name of signatory"
            className="mt-1"
            data-testid="quotation-pad-name"
          />
        </div>
        <Label className="text-sm">Sign in the box below</Label>
        <div className="border-2 border-gray-300 rounded-md bg-white mt-1 mb-3 touch-none">
          <SignatureCanvas
            ref={padRef}
            penColor="#1f2937"
            clearOnResize={false}
            canvasProps={{
              className: 'w-full h-32 rounded-md',
              'data-testid': 'quotation-pad-canvas',
            }}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearPad}
            disabled={saving}
            data-testid="quotation-pad-clear"
          >
            <Eraser size={14} className="mr-1" /> Clear
          </Button>
          <Button
            size="sm"
            onClick={saveSignature}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="quotation-pad-save"
          >
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save signature'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuotationSignaturePad;
